import { useQuery } from "@apollo/client";
import { Autocomplete, TextField } from "@mui/material";
import { gql } from "generated-graphql";
import { useDebounce } from "hooks/useDebounce";
import { useMemo, useState } from "react";

const CERS_ANALYTICS_TENANT_OPTIONS = gql(/* GraphQL */ `
  query CersAnalyticsTenantOptions($search: String) {
    tenants(search: $search, page: 0, pageSize: 25) {
      items {
        id
        name
      }
      count
    }
  }
`);

export interface TenantOption {
  id: string;
  name: string;
}

export function TenantAutocomplete({
  value,
  onChange,
}: {
  value: TenantOption | null;
  onChange: (tenant: TenantOption | null) => void;
}) {
  const [inputValue, setInputValue] = useState("");
  // Debounce so we don't fire a tenants query on every keystroke.
  const [debouncedInput, isWaiting] = useDebounce(inputValue, 300);
  const { data, loading } = useQuery(CERS_ANALYTICS_TENANT_OPTIONS, {
    variables: { search: debouncedInput || undefined },
    fetchPolicy: "cache-and-network",
  });

  const options: TenantOption[] = useMemo(
    () => (data?.tenants.items ?? []).map((t) => ({ id: t.id, name: t.name })),
    [data?.tenants.items]
  );

  return (
    <Autocomplete
      size="small"
      sx={{ width: 280 }}
      value={value}
      onChange={(_e, next) => onChange(next)}
      inputValue={inputValue}
      onInputChange={(_e, next) => setInputValue(next)}
      options={options}
      loading={loading || isWaiting}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderInput={(params) => (
        <TextField {...params} label="Organization" placeholder="All organizations" />
      )}
    />
  );
}
